import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCaptchaLoader } from "@/hooks/use-captcha-loader";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { ZodFormSchema, useFormSchemaLoader } from "@/hooks/use-form-schema-loader";
import { useFormSubmit } from "@/hooks/use-form-submit";
import { FormField as FormFieldType, moonbase } from "@/lib/moonbase";
import { NotFoundPage } from "@/pages";
import { zodResolver } from "@hookform/resolvers/zod";
import invariant from "@moonbaseai/invariant";
import { useRef, useState } from "react";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import z, { ZodType } from "zod";

function getFieldDef(zodField: ZodType): FormFieldType {
  const meta = zodField.getMeta();
  invariant(meta !== undefined);

  return meta as FormFieldType;
}

type FormFieldProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: ControllerRenderProps<{ [x: string]: any }, string>;
  fieldDef: FormFieldType;
};

type SelectFieldProps = FormFieldProps & { fieldDef: FormFieldType & { type: "choice" | "stage" } };

function CheckboxField({ field, fieldDef }: FormFieldProps) {
  return (
    <FormItem className="flex items-start gap-2 self-stretch">
      <FormControl>
        <Checkbox
          key={fieldDef.id}
          name={field.name}
          checked={field.value}
          value={1}
          onCheckedChange={field.onChange}
        />
      </FormControl>
      <div className="flex flex-col gap-2 pt-px">
        <FormLabel>{fieldDef.name}</FormLabel>
        <FormMessage />
        {fieldDef.description && <FormDescription>{fieldDef.description}</FormDescription>}
      </div>
    </FormItem>
  );
}

function SelectField({ field, fieldDef }: SelectFieldProps) {
  return (
    <FormItem className="self-stretch">
      <FormLabel>{fieldDef.name}</FormLabel>
      <FormControl>
        <Select {...field} onValueChange={field.onChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fieldDef.options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormControl>
      {fieldDef.description && <FormDescription>{fieldDef.description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
}

function MultipleSelectField({ field, fieldDef }: SelectFieldProps) {
  return (
    <FormItem className="self-stretch">
      <FormLabel>{fieldDef.name}</FormLabel>
      <FormControl>
        <div className="flex flex-col gap-2">
          {fieldDef.options.map((option) => {
            return (
              <div className="flex items-center gap-2" key={option.id}>
                <Checkbox
                  id={option.id}
                  name={field.name}
                  value={option.id}
                  checked={field.value?.includes(option.id)}
                  onCheckedChange={(checked) => {
                    const currentValues = field.value || [];
                    return checked
                      ? field.onChange([...currentValues, option.id])
                      : field.onChange(currentValues.filter((v: string) => v !== option.id));
                  }}
                />
                <label htmlFor={option.id} className="text-sm leading-none">
                  {option.label}
                </label>
              </div>
            );
          })}
        </div>
      </FormControl>
      {fieldDef.description && <FormDescription>{fieldDef.description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
}

function TextInputField({ field, fieldDef }: FormFieldProps) {
  const inputProps: React.ComponentProps<"input"> = {};

  if (fieldDef.type === "email") {
    inputProps.type = "email";
  } else if (fieldDef.type === "telephone_number") {
    inputProps.type = "tel";
  } else if (fieldDef.type === "uri") {
    inputProps.type = "text";
    inputProps.inputMode = "url";
    inputProps.onBlur = () => {
      const value = field.value;
      if (typeof value === "string" && value.trim().length > 0) {
        const normalized = moonbase.normalize.normalizeUrl(value);
        if (normalized !== null && normalized !== value) {
          field.onChange(normalized);
        }
      }
    };
  } else if (fieldDef.type === "datetime") {
    inputProps.type = "datetime-local";
  } else if (fieldDef.type === "date") {
    inputProps.type = "date";
  }

  return (
    <FormItem className="self-stretch">
      <FormLabel>{fieldDef.name}</FormLabel>
      <FormControl>
        {fieldDef.type === "text" && fieldDef.subType === "multi_line" ? (
          <Textarea {...field} />
        ) : (
          <Input {...field} {...inputProps} className="block w-full" />
        )}
      </FormControl>
      {fieldDef.description && <FormDescription>{fieldDef.description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
}

function FormContainer({
  formId,
  formName,
  formDescription,
  formSchema,
  captchaWidgetId,
}: {
  formId: string;
  formName: string;
  formDescription: string | null;
  formSchema: ZodFormSchema;
  captchaWidgetId: string | null;
}) {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const { handleSubmit } = useFormSubmit({
    captchaSiteKey: moonbase.config.hcaptchaSiteKey!,
    captchaWidgetId,
    formId,
  });

  const onSubmit = async () => {
    const theForm = formRef.current;
    if (!theForm) return;
    setSubmitting(true);
    const formData = new FormData(theForm);
    await handleSubmit(formData, form.setError);
    setSubmitting(false);
  };

  useDocumentTitle(formName);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex w-full flex-col items-start gap-8 bg-white sm:max-w-[550px] sm:rounded-xl sm:p-12 sm:shadow-xl sm:ring-1 sm:ring-black/5 dark:bg-transparent dark:shadow-none dark:ring-0"
        ref={formRef}
      >
        <div className="mb-2">
          <h3 className="scroll-m-20 text-xl font-semibold tracking-tight">{formName}</h3>
          {formDescription && (
            <p className="text-muted-foreground mt-1 text-sm">{formDescription}</p>
          )}
        </div>
        {Object.entries(formSchema.shape).map(([name, zodField]) => (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field }) => {
              const fieldDef = getFieldDef(zodField);

              switch (fieldDef.type) {
                case "boolean":
                  return <CheckboxField field={field} fieldDef={fieldDef} />;
                case "choice":
                  if (fieldDef.cardinality === "many") {
                    return <MultipleSelectField field={field} fieldDef={fieldDef} />;
                  } else {
                    return <SelectField field={field} fieldDef={fieldDef} />;
                  }
                default:
                  return <TextInputField field={field} fieldDef={fieldDef} />;
              }
            }}
          />
        ))}
        <Button type="submit" disabled={submitting}>
          Submit
        </Button>
      </form>
    </Form>
  );
}

function FormPage() {
  const { formId } = useParams<{ formId: string }>();

  const result = useFormSchemaLoader(formId);
  const { captchaRef, captchaWidgetId } = useCaptchaLoader();

  if (result.status === "error") {
    throw result.error;
  }

  if (result.status === "not_found") {
    return <NotFoundPage />;
  }

  return (
    <>
      <div className="mx-auto grid min-h-dvh place-items-center px-4 py-16">
        {result.status === "loading" ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : (
          <FormContainer
            formId={formId!}
            formName={result.formName}
            formDescription={result.formDescription}
            formSchema={result.formSchema}
            captchaWidgetId={captchaWidgetId}
          />
        )}
        <div ref={captchaRef} />
      </div>
    </>
  );
}

export { FormPage };
